import React, {useState, useEffect, useLayoutEffect, useRef} from 'react';
import FacebookLoader from '../loader';

const {useSelector, useA11yButton, useHotkeys, useUnique} = polyphony.hooks;
const {prefixCSS} = polyphony.css;
const {executeAfter, combineRefs} = polyphony.util;
const {postWPJSON} = polyphony.networking;

const emailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/; // eslint-disable-line

// This is a stub
const callConstContact = (email) => {
    return postWPJSON('/polyphony-child/v1/signup_email', {email});
};

const svgs = {
    checkmark: <svg style={{width: '100%', height: '100%', padding: 10, boxSizing: 'border-box'}} viewBox="0 0 448 512"><path fill="currentColor" d="M413.505 91.951L133.49 371.966l-98.995-98.995c-4.686-4.686-12.284-4.686-16.971 0L6.211 284.284c-4.686 4.686-4.686 12.284 0 16.971l118.794 118.794c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-11.314-11.314c-4.686-4.686-12.284-4.686-16.97 0z" /></svg>,
    chevron: <svg key="svg" height={20} width={20} viewBox="0 0 256 512"><path fill="currentColor" d="M24.707 38.101L4.908 57.899c-4.686 4.686-4.686 12.284 0 16.971L185.607 256 4.908 437.13c-4.686 4.686-4.686 12.284 0 16.971L24.707 473.9c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L41.678 38.101c-4.687-4.687-12.285-4.687-16.971 0z" /></svg>
};

const styles = {
    statusMessage: {
        textAlign: 'center',
        fontSize: '75%',
        padding: 10,
        boxSizing: 'border-box',
        width: '100%',
        height: '100%'
    },
    container: prefixCSS({
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        width: '100%',
        height: 44,
        boxSizing: 'border-box',
        paddingLeft: 5
    }),
    input: {
        width: 'calc(100% - 42px)',
        padding: 5,
        boxSizing: 'border-box'
    }
};

const NewsletterSignUpBlock = ({gutenberg:{isEditor}}) => {
    const unique = useUnique();
    const control_color = useSelector(s => s.newsletter.control_color);

    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasSubmitted, setSubmitted] = useState(false);

    const [isOpen, setOpen] = useState(false);
    const [text, setText] = useState('');

    const [{hover}, ref] = useA11yButton();

    const textRef = useRef();

    useLayoutEffect(() => {
        if (textRef.current) {
            textRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (hasSubmitted) {
            return executeAfter(() => {
                setText('');
                setError(null);
                setSubmitted(false);
            }, 2500);
        }
        return () => {};
    }, [hasSubmitted]);

    const onSubmit = () => {
        if (emailRegex.test(text)) {
            setLoading(true);
            callConstContact(text).then(status => {
                if (!status.success) {
                    setError(status.message);
                }
                setLoading(false);
                setSubmitted(true);
                setOpen(false);
            });
        } else {
            setSubmitted(true);
            setError(`${text} is not a valid email address`);
            let el = textRef.current;
            el.style.color = 'red';
            let time = 100;
            executeAfter(() => el.style.color = 'red', time);
            executeAfter(() => el.style.color = 'black', time + time);
        }
    };

    const hkref = useHotkeys({
        Enter: onSubmit
    }, [onSubmit]);

    if (isLoading) {
        return <div role="none" style={prefixCSS({display: 'flex', justifyContent: 'center', height: 44})}><FacebookLoader /></div>;
    }

    if (isOpen) {
        return <>
            {error && <div key="error-message" aria-live='assertive' style={styles.statusMessage}>There was an error signing you up for our newsletter: {error}</div>}
            <div key="control" style={{...styles.container, border: `1px solid ${control_color}`}} role="application">
                <input type="text" ref={combineRefs([textRef, hkref])} value={text} onChange={e => setText(e.target.value)} placeholder="your@email.com" style={styles.input}/>
                <a onClick={onSubmit} ref={ref} style={{cursor: 'pointer', width: 42, height: 42, backgroundColor: hover ? 'white' : control_color, color: hover ? control_color : 'white', borderLeft: `1px solid ${control_color}`, boxSizing: 'border-box'}}>{svgs.checkmark}</a>
            </div>
        </>;
    }

    const s = {
        a: prefixCSS({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: isEditor ? 'not-allowed' : 'pointer',
            height: 44,
            width: '100%',
            backgroundColor: hover ? 'white' : control_color,
            border: `1px solid ${control_color}`,
            boxSizing: 'border-box',
            fontSize: 25
        }),
        span: prefixCSS({
            fontSize: 'inherit',
            color: hover ? control_color : 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        })
    };

    return !error && hasSubmitted
        ? <div style={styles.statusMessage} aria-live='assertive'>Successfully signed up {text}.</div>
        : <a ref={ref} role="button" aria-labelledby={unique} onClick={isEditor ? undefined : () => setOpen(true)} style={s.a}>
              <span key="span" id={unique} style={s.span}>Enter Email {svgs.chevron}</span>
          </a>;
};

NewsletterSignUpBlock.title = "Newsletter Sign Up";
NewsletterSignUpBlock.icon = 'wp';
NewsletterSignUpBlock.category = 'layout';

export default NewsletterSignUpBlock;

